import { errorsApis } from '@gleamer/apis';
import { Internal } from '@gleamer/types';
import React from 'react';
import { Link } from 'react-router-dom';
import { useReviewSession } from '../../hooks/useReviewSession';

const { isNoMoreItemsError } = errorsApis;

type LabelTask = Internal.LabelTask;

type TaskLinkProps = {
  task: Pick<LabelTask, 'taskId' | 'reviewer'>;
  /** What the link shows; the task's id by default. */
  children?: React.ReactNode;
  className?: string;
};

function LabelerTaskLink({ task, children, className }: TaskLinkProps) {
  return (
    <Link
      to={`/ohif/label?task=${task.taskId}`}
      className={className ?? 'cursor-pointer'}
    >
      {children ?? task.taskId}
    </Link>
  );
}

function ReviewerTaskLink({ task, children, className }: TaskLinkProps) {
  const { data, isLoading, error } = useReviewSession({ taskId: task.taskId });

  if (isNoMoreItemsError(error as Error)) {
    return (
      <Link
        to={`/review-session/${task.taskId}`}
        className={className ?? 'cursor-pointer'}
      >
        {children ?? task.taskId}
      </Link>
    );
  }

  if (isLoading || error) {
    return (
      <span className={className} aria-disabled>
        {children ?? task.taskId}
      </span>
    );
  }

  const urlParams = new URLSearchParams();
  urlParams.append('task', `${task.taskId}`);
  urlParams.append('reviewMode', data.reviewMode);
  if (data.itemToReviewId) {
    urlParams.append('item', data.itemToReviewId);
  }
  if (data.labeler) {
    urlParams.append('labeledBy', data.labeler);
  }

  return (
    <Link
      to={`/ohif/review/review?${urlParams.toString()}`}
      className={className ?? 'cursor-pointer'}
    >
      {children ?? task.taskId}
    </Link>
  );
}

export function TaskLink(props: TaskLinkProps) {
  if (props.task.reviewer) {
    return <ReviewerTaskLink {...props} />;
  }

  return <LabelerTaskLink {...props} />;
}
